import * as React from "react";
import {Types}    from "@Common/types";

import "./multi-input-modal.scss";

import {cs}                from "@Common/react/chain-services";
import {consumeContext}    from "@Common/react/context";
import {scopedState}       from "@Common/react/scoped-state";
import {UseState}          from "@Common/react/use-state";
import {ConfirmDialog}     from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {ModalService3}     from "@Common/ui-components/modal-service-3/modal-service-3";
import {needsConfirmation} from "@Common/ui-components/needs-confirmation/needs-confirmation";
import {Responsive}        from "@Common/ui-components/responsive";
import {setNestedValue}    from "@Common/utils/arr-path";
import {equalDeep}         from "@Common/utils/objects";
import {ModalLayout}       from "../../../common/modal-layout/modal-layout";

export const MultiInputModal:<Keys extends string>(props:Types.Xpresso.Account.Common.MultiInputModal.MultiInputModalProps<Keys>) => any = ({
    next: rootNext,    
    title,
    labelLocation = "top",
    onSave,
    reload,
    urlParam,
}) => cs(
    consumeContext("apis"),
    ["uploading", (_, next) => UseState({next})],
    // @ts-ignore
    ["responsive", (_, next) => <Responsive {...{
        sizes: {
            mobile: 500,
        },
        next,
    }}/>],
    ["isDirty", (_, next) => UseState({next, initValue: false})],
    ["confirmDialog", ({}, next) => ConfirmDialog({next})],    
    ["modal", ({responsive, isDirty, confirmDialog}, next) => ModalService3({
        xdQaName: "Multi Input Modal",
        onPassiveClose: async () => {
            if(isDirty.value) {
                return await needsConfirmation({
                    confirmDialog,
                });               
            }
        },
        width: responsive.lt("mobile") ? 360 : "fit-content",
        next,
        urlParam,
        rootNext,
    })],
    ["data", ({modal}, next) => next(modal.params.data)],
    ["state", ({data}, next) => UseState({next, initValue: data})],
    ["errorTop", (_, next) => UseState({next})],
    ({uploading, state, modal, data, errorTop, isDirty, confirmDialog}, next) => ModalLayout({
        title,
        content: next(),
        buttons: [
            {
                label: "Cancel",
                onClick: async () => {
                    if(isDirty.value) {
                        const modalShouldClose = await needsConfirmation({confirmDialog}); 
                        
                        if(!modalShouldClose) {
                            return
                        } 
                    }
                    state.onChange(data);
                    modal.close();
                },
            },
            {
                label: "Save",
                disabled: equalDeep(data, state.value) || uploading.value,
                primary: true,
                onClick: async () => {
                    uploading.onChange(true);
                    clearErrors({});
                    const result = await onSave(state.value.reduce(dataReducer,{}));
                    if (result?.errorMessage) {
                        alert(result.errorMessage);
                    } else if (result?.errorInfo) {
                        const errors = JSON.parse(result?.errorInfo);
                        errors.map((error) => {
                            const field = document.querySelectorAll('[mapping=' + error.mapping + ']');
                            const errorMessage = document.createElement("div");
                            const parentElement = field[0].parentElement;
                            errorMessage.innerText = error.message;
                            errorMessage.classList.add("errorMessage");
                            errorMessage.setAttribute("errorMapping", error.mapping);
                            errorMessage.setAttribute("xd-qa-type", "label");
                            errorMessage.setAttribute("xd-qa-name", getQaName(error.mapping) + " Error")
                            field[0].classList.add("error");
                            if(labelLocation === "left")
                            {
                                parentElement.insertBefore(errorMessage, field[0].nextSibling);
                            }
                            else
                            {
                                parentElement.appendChild(errorMessage);
                            }
                        });
                    } else if (result?.errorTop) {
                        errorTop.onChange(result.errorTop);
                    } else {
                        reload && reload();
                        modal.close();
                    }
                    uploading.onChange(false);
                },
            },
        ],
    }),
    ({state, data, errorTop, isDirty}) => (
        <div className="multi-input-modal-f6g">
            {errorTop.value && (
                <div className="error-top">
                    {errorTop.value}
                </div>
            )}
            {data?.length > 0 && (
                labelLocation === "left" ? (
                    <div className="left-allign">
                        <div className="label-column">
                            {data.map((datum, index) => (
                                <div className="label" key={index} xd-qa-type="label" xd-qa-name={getQaName(datum.mapping)}>{datum.label}</div>
                            ))}
                        </div>
                        <div className="value-column">
                            {data.map((datum, index) => (
                                <input {...{
                                    key: index,
                                    value: scopedState(state, [index, "value"]).value, 
                                    onChange: (e) => {
                                        let value = e.target.value;
                                        const mapping = e.target.attributes.mapping.value;
                                        const newState = setNestedValue(state.value, [index, "value"], value);
                                        isDirty.onChange(!equalDeep(data, newState));
                                        state.onChange(newState);
                                        clearErrors({mapping});                                       
                                    },
                                    className: "value",
                                    maxLength: datum?.maxLength,
                                    "mapping": datum.mapping,
                                    "xd-qa-type": "textbox",
                                    "xd-qa-name": getQaName(datum.mapping),
                                }}/>
                            ))}
                        </div>
                    </div>
                ) : (
                    data.map((datum, index) => (
                        <div className="input-card" key={index}>
                            <div className="label">{datum.label}</div>
                            <input {...{
                                value: scopedState(state, [index, "value"]).value, 
                                onChange: (e) => {
                                    let value = e.target.value;
                                    const mapping = e.target.attributes.mapping.value;
                                    const newState = setNestedValue(state.value, [index, "value"], value);
                                    isDirty.onChange(!equalDeep(data, newState));
                                    state.onChange(newState);
                                    clearErrors({mapping});                           
                                },
                                className: "value",
                                maxLength: datum?.maxLength,
                                "mapping": datum.mapping,
                                "xd-qa-type": "textbox",
                                "xd-qa-name": getQaName(datum.mapping),
                            }}/>
                        </div>
                    ))
                )
            )}
        </div>
    )
);

const dataReducer =
  <Keys extends string,>(obj:{[key:string]: string}, {mapping, value}:Types.Xpresso.Account.Common.MultiInputModal.Data<Keys>) => ({[mapping]: value.trim(), ...obj});

const clearErrors = ({mapping=undefined}) => {
    let errors = [];
    let errorMessages = [];
    if(mapping)
    {
        errors = Array.from(document.querySelectorAll('[mapping=' + mapping + ']'));
        errorMessages = Array.from(document.querySelectorAll('[errorMapping=' + mapping + ']'));
    }
    else
    {
        errors = Array.from(document.getElementsByClassName("error"));
        errorMessages = Array.from(document.getElementsByClassName("errorMessage"));
    }
    errors.map((error) => error.classList.remove("error"));
    errorMessages.map((errorMessage) => errorMessage.remove());
}

const getQaName = (data) => {
    const result = data.replace(/([A-Z0-9])/g, " $1");
    return result.charAt(0).toUpperCase() + result.slice(1);
}